import React, { memo, useState, useMemo } from 'react'
import PropTypes from 'prop-types'

/**
 * ProjectRiskTable - Displays project-wise risk observations
 *
 * @param {Array} observations - Array of project observations
 */
const ProjectRiskTable = memo(({ observations }) => {
	const [sortField, setSortField] = useState('risk_score')
	const [sortDirection, setSortDirection] = useState('desc')
	const [expandedRows, setExpandedRows] = useState(new Set())

	if (!observations || observations.length === 0) {
		return null
	}

	const handleSort = (field) => {
		if (sortField === field) {
			setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
		} else {
			setSortField(field)
			setSortDirection('desc')
		}
	}

	const toggleRowExpansion = (projectId) => {
		const newExpanded = new Set(expandedRows)
		if (newExpanded.has(projectId)) {
			newExpanded.delete(projectId)
		} else {
			newExpanded.add(projectId)
		}
		setExpandedRows(newExpanded)
	}

	const sortedObservations = useMemo(() => {
		return [...observations].sort((a, b) => {
			let aVal = a[sortField]
			let bVal = b[sortField]

			if (sortField === 'risk_level') {
				const levelOrder = { high: 3, medium: 2, low: 1, unknown: 0 }
				aVal = levelOrder[aVal?.toLowerCase()] || 0
				bVal = levelOrder[bVal?.toLowerCase()] || 0
			}

			if (sortDirection === 'asc') {
				return aVal > bVal ? 1 : -1
			}
			return aVal < bVal ? 1 : -1
		})
	}, [observations, sortField, sortDirection])

	const getRiskLevelBadge = (level) => {
		const levelLower = level?.toLowerCase()
		return (
			<span className={`project-risk-table__badge project-risk-table__badge--${levelLower || 'unknown'}`}>
				{level || 'Unknown'}
			</span>
		)
	}

	const getUrgencyBadge = (urgency) => {
		const urgencyMap = {
			immediate: 'danger',
			'this week': 'warning',
			'this month': 'info',
			monitor: 'success'
		}
		const variant = urgencyMap[urgency?.toLowerCase()] || 'default'
		return (
			<span className={`project-risk-table__urgency project-risk-table__urgency--${variant}`}>
				{urgency || 'N/A'}
			</span>
		)
	}

	const SortIcon = ({ field }) => {
		if (sortField !== field) {
			return (
				<svg className="project-risk-table__sort-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
					<path d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
				</svg>
			)
		}
		return (
			<svg className={`project-risk-table__sort-icon project-risk-table__sort-icon--active`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
				{sortDirection === 'asc' ? (
					<path d="M5 15l7-7 7 7" />
				) : (
					<path d="M19 9l-7 7-7-7" />
				)}
			</svg>
		)
	}

	return (
		<div className="project-risk-table">
			<div className="project-risk-table__header">
				<h3 className="project-risk-table__title">
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
						<path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
					</svg>
					Project-wise Risk Summary
				</h3>
				<span className="project-risk-table__count">{observations.length} projects</span>
			</div>

			<div className="project-risk-table__wrapper">
				<table className="project-risk-table__table">
					<thead>
						<tr>
							<th onClick={() => handleSort('project_name')} className="project-risk-table__th--sortable">
								Project <SortIcon field="project_name" />
							</th>
							<th onClick={() => handleSort('risk_level')} className="project-risk-table__th--sortable">
								Risk Level <SortIcon field="risk_level" />
							</th>
							<th onClick={() => handleSort('risk_score')} className="project-risk-table__th--sortable">
								Score <SortIcon field="risk_score" />
							</th>
							<th>Risk Areas</th>
							<th onClick={() => handleSort('urgency')} className="project-risk-table__th--sortable">
								Urgency <SortIcon field="urgency" />
							</th>
							<th></th>
						</tr>
					</thead>
					<tbody>
						{sortedObservations.map((obs) => (
							<React.Fragment key={obs.project_id}>
								<tr className={expandedRows.has(obs.project_id) ? 'project-risk-table__row--expanded' : ''}>
									<td className="project-risk-table__project-name">
										{obs.project_name}
									</td>
									<td>{getRiskLevelBadge(obs.risk_level)}</td>
									<td>
										<span className={`project-risk-table__score project-risk-table__score--${obs.risk_level?.toLowerCase()}`}>
											{obs.risk_score}
										</span>
									</td>
									<td>
										<div className="project-risk-table__risk-areas">
											{(obs.risk_areas || []).slice(0, 2).map((area, idx) => (
												<span key={idx} className="project-risk-table__area-tag">
													{area}
												</span>
											))}
											{(obs.risk_areas || []).length > 2 && (
												<span className="project-risk-table__area-more">
													+{obs.risk_areas.length - 2}
												</span>
											)}
										</div>
									</td>
									<td>{getUrgencyBadge(obs.urgency)}</td>
									<td>
										<button
											className="project-risk-table__expand-btn"
											onClick={() => toggleRowExpansion(obs.project_id)}
											aria-label={expandedRows.has(obs.project_id) ? 'Collapse' : 'Expand'}
										>
											<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
												{expandedRows.has(obs.project_id) ? (
													<path d="M5 15l7-7 7 7" />
												) : (
													<path d="M19 9l-7 7-7-7" />
												)}
											</svg>
										</button>
									</td>
								</tr>
								{expandedRows.has(obs.project_id) && (
									<tr className="project-risk-table__details-row">
										<td colSpan="6">
											<div className="project-risk-table__details">
												{obs.key_findings && (
													<div className="project-risk-table__detail-section">
														<h4>Key Findings</h4>
														<p>{obs.key_findings}</p>
													</div>
												)}
												{obs.recommendations && obs.recommendations.length > 0 && (
													<div className="project-risk-table__detail-section">
														<h4>Recommendations</h4>
														<ul>
															{obs.recommendations.map((rec, idx) => (
																<li key={idx}>{rec}</li>
															))}
														</ul>
													</div>
												)}
												{obs.risk_areas && obs.risk_areas.length > 0 && (
													<div className="project-risk-table__detail-section">
														<h4>All Risk Areas</h4>
														<div className="project-risk-table__all-areas">
															{obs.risk_areas.map((area, idx) => (
																<span key={idx} className="project-risk-table__area-tag">
																	{area}
																</span>
															))}
														</div>
													</div>
												)}
											</div>
										</td>
									</tr>
								)}
							</React.Fragment>
						))}
					</tbody>
				</table>
			</div>
		</div>
	)
})

ProjectRiskTable.displayName = 'ProjectRiskTable'

ProjectRiskTable.propTypes = {
	observations: PropTypes.arrayOf(
		PropTypes.shape({
			project_id: PropTypes.number,
			project_name: PropTypes.string,
			risk_level: PropTypes.string,
			risk_score: PropTypes.number,
			risk_areas: PropTypes.arrayOf(PropTypes.string),
			key_findings: PropTypes.string,
			recommendations: PropTypes.arrayOf(PropTypes.string),
			urgency: PropTypes.string
		})
	)
}

export default ProjectRiskTable
